import { PerformanceDatapoint } from 'owa-analytics';
import { AttachmentFileType, CloudFile } from 'owa-attachment-file-types';
import type { AttachmentSelectedFolderInfo } from 'owa-attachment-folder-picker';
import {
    AttachmentFullViewState,
    AttachmentState,
    setActionCompletePercent,
    stopFakeProgressIndicator,
} from 'owa-attachment-full-data';
import type { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import { getMimeTypeForOneDriveItem } from 'owa-fileprovider-items-service';
import type { OneDriveUploadedFile, ProgressHandler } from 'owa-fileprovider-services';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import type AttachmentInfoResponseMessage from 'owa-service/lib/contract/AttachmentInfoResponseMessage';
import type AttachmentType from 'owa-service/lib/contract/AttachmentType';
import { parse as parseUrl } from 'url';
import createReferenceAttachmentFromAttachmentDataProviderService from '../services/createReferenceAttachmentFromAttachmentDataProvider';
import { uploadLocalFile } from '../utils/uploadLocalFile';
import type { CreateAttachmentDependencies } from './createAttachment';
import {
    onAttachmentCreationError,
    onAttachmentCreationSuccess,
} from './helpers/onAttachmentCreationHandlers';

export function uploadLocalFileAndCreateReferenceAttachment(
    parentItemId: ClientItemId,
    attachmentId: ClientAttachmentId,
    clientAttachment: AttachmentType,
    cancellationId: string,
    folderInfoToUploadTo: AttachmentSelectedFolderInfo,
    backingObject: Blob,
    attachmentState: AttachmentState,
    dependencies: CreateAttachmentDependencies
): { onCompletePromise: Promise<AttachmentType | null>; cancel: () => void } {
    const datapoint = new PerformanceDatapoint('UploadLocalFileAndCreateCloudyAttachment', {
        timeout: 300000, // five minutes
    });

    const progressHandler: ProgressHandler = ({ loaded, total }) => {
        const completePercent = loaded / total;
        setActionCompletePercent(<AttachmentFullViewState>attachmentState, completePercent);
    };

    // 1- Upload file
    const [{ uploadPromise, cancel }, oneDriveProvider] = uploadLocalFile(
        clientAttachment.Name,
        backingObject,
        folderInfoToUploadTo,
        progressHandler
    );
    const {
        model: { Id: providerId, EndPointUrl: providerEndpointUrl, Type: providerType },
    } = oneDriveProvider;

    let hasBeenCanceled = false;
    const onCancel = () => {
        hasBeenCanceled = true;
        cancel();
    };

    const onCompletePromise = async () => {
        let errorToLog: any = null;
        try {
            const {
                errorResponse: uploadErrorResponse,
                result: uploadResult,
            } = await uploadPromise;

            if (uploadErrorResponse) {
                const errorResponseBody = await uploadErrorResponse.text();
                throw new Error(
                    `Unable to upload file with status code: ${uploadErrorResponse.status}; response: ${errorResponseBody}`
                );
            }

            // 2- Create attachment using the uploaded one drive file
            const cloudFile = createCloudFileFromOneDriveUploadedFile(
                uploadResult,
                providerId,
                providerType,
                providerEndpointUrl
            );

            const createAttachmentResponse = await createReferenceAttachmentFromAttachmentDataProviderService(
                parentItemId,
                cancellationId,
                cloudFile
            );

            const responseMessage: AttachmentInfoResponseMessage =
                createAttachmentResponse.ResponseMessages.Items[0];
            if (responseMessage && responseMessage.Attachments.length > 0) {
                stopFakeProgressIndicator(attachmentState.attachmentId, true);

                const serverAttachment = responseMessage.Attachments[0];
                onAttachmentCreationSuccess(
                    attachmentState,
                    attachmentId,
                    clientAttachment,
                    serverAttachment,
                    parentItemId,
                    createAttachmentResponse.SharingInformation,
                    dependencies.attachmentWellViewState
                );

                return serverAttachment;
            } else {
                throw new Error(responseMessage.MessageText);
            }
        } catch (error) {
            stopFakeProgressIndicator(attachmentState.attachmentId, false);
            onAttachmentCreationError(attachmentState, dependencies.attachmentWellViewState);

            if (hasBeenCanceled) {
                // If the upload has been canceled then we don't throw the error
                // instead return a null attachment.
                return null;
            }

            errorToLog = error;
            throw error;
        } finally {
            if (errorToLog) {
                // We have this separate datapoint for error so that we always log it.
                // As `UploadLocalFileAndCreateCloudyAttachment` is sampled so all errors won't be logged.
                new PerformanceDatapoint('UploadLocalFileAndCreateCloudyAttachmentError', {
                    isCore: true,
                }).endWithError(errorToLog);

                datapoint.endWithError(errorToLog);
            } else {
                datapoint.end();
            }
        }
    };

    return { onCompletePromise: onCompletePromise(), cancel: onCancel };
}

function createCloudFileFromOneDriveUploadedFile(
    file: OneDriveUploadedFile,
    providerId: string,
    providerType: AttachmentDataProviderType,
    endpointUrl: string
): CloudFile {
    return {
        fileType: AttachmentFileType.Cloud,
        name: file.name,
        size: file.size,
        providerId: providerId,
        providerType: providerType,
        location: getLocationForFile(file, providerType),
        providerEndpointUrl: endpointUrl,
        isFolder: false,
        fileId: file.id,
        fileParentId: file.parentReference.id,
        thumbnailUrl: '',
        previewUrl: '',
        type: getMimeTypeForOneDriveItem(file as any),
    };
}

function getLocationForFile(file: OneDriveUploadedFile, providerType: AttachmentDataProviderType) {
    if (providerType === AttachmentDataProviderType.OneDriveConsumer) {
        return `${file.parentReference.path}/${file.name}`;
    } else {
        return parseUrl(file.webUrl).path;
    }
}
